import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  MenuItem, Select as MuiSelect, FormControl, InputLabel, Divider,
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { Pencil, PowerOff, Trash2, Save, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { dataService } from "../../../Services/data/data.service";
import { flowBoxDeviceStatus, flowBoxButtonAction, externalApiTypes } from "../../../Config/constants";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import Button from "../../Shared/Buttons/Button";
import BackButton from "../../Shared/Buttons/BackButton";
import Label from "../../Shared/Label";

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const STATUS_INFO = {
  [flowBoxDeviceStatus.REGISTERED]:   { tKey: "flowBox.status.registered",   color: "#f59e0b" },
  [flowBoxDeviceStatus.ACTIVE]:       { tKey: "flowBox.status.active",        color: "#22c55e" },
  [flowBoxDeviceStatus.DEREGISTERED]: { tKey: "flowBox.status.deregistered",  color: "#ef4444" },
  [flowBoxDeviceStatus.INACTIVE]:     { tKey: "flowBox.status.inactive",      color: "#9ca3af" },
};

const formatDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  return isNaN(d) ? "-" : d.toLocaleString();
};

// ──────────────────────────────────────────────────────────────────────────────
// ButtonSlot — one button row in the mapping panel
// ──────────────────────────────────────────────────────────────────────────────
function ButtonSlot({ index, mapping, externalApis, companyId, userId, deviceId, onSaved }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [mode, setMode] = useState(null);

  const ACTION_OPTS_STOPWATCH = [
    { value: flowBoxButtonAction.TOGGLE, label: t("flowBox.action.toggle") },
    { value: flowBoxButtonAction.START,  label: t("flowBox.action.start") },
    { value: flowBoxButtonAction.STOP,   label: t("flowBox.action.stop") },
  ];

  // "use-existing" state
  const [selectedApiId, setSelectedApiId] = useState(mapping?.externalApiId ?? "");
  const [apiActionId, setApiActionId] = useState(mapping?.apiActionId ?? flowBoxButtonAction.MEASURE);

  // "create-trigger" state
  const [triggerName, setTriggerName] = useState("");
  const [triggerTypeId, setTriggerTypeId] = useState(externalApiTypes.DATA);
  const [newTriggerAction, setNewTriggerAction] = useState(flowBoxButtonAction.TOGGLE);

  // Cascading project -> detail selectors for "create-trigger"
  const [projects, setProjects] = useState([]);
  const [selectedProjectQr, setSelectedProjectQr] = useState("");
  const [detailOptions, setDetailOptions] = useState([]);
  const [projectDetailId, setProjectDetailId] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [saving, setSaving] = useState(false);

  const selectedApi = externalApis.find((a) => a.id === selectedApiId);
  const isStopwatchExisting = selectedApi?.externalApiTypeId === externalApiTypes.STOPWATCH;
  const isStopwatchNew = triggerTypeId === externalApiTypes.STOPWATCH;

  // Load projects when "create-trigger" mode opens
  useEffect(() => {
    if (mode === "create-trigger" && projects.length === 0) {
      setLoadingProjects(true);
      dataService.getUserCompanyProjectOverview(userId)
        .then((ps) => setProjects(ps || []))
        .catch(() => setProjects([]))
        .finally(() => setLoadingProjects(false));
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load details when a project is selected
  useEffect(() => {
    if (!selectedProjectQr) { setDetailOptions([]); setProjectDetailId(""); return; }
    setLoadingDetails(true);
    dataService.getProjectDetailsByQr(selectedProjectQr)
      .then((details) => {
        const opts = (details || []).map((d) => ({
          id: d.id,
          label: d.description || `Field #${d.id}`,
          typeId: d.typeId,
        }));
        setDetailOptions(opts);
      })
      .catch(() => setDetailOptions([]))
      .finally(() => setLoadingDetails(false));
  }, [selectedProjectQr]);

  const openUseExisting = () => {
    setMode("use-existing");
    setSelectedApiId(mapping?.externalApiId ?? "");
    setApiActionId(mapping?.apiActionId ?? flowBoxButtonAction.MEASURE);
  };

  const openCreateTrigger = () => {
    setMode("create-trigger");
    setTriggerName("");
    setTriggerTypeId(externalApiTypes.DATA);
    setSelectedProjectQr("");
    setProjectDetailId("");
    setDetailOptions([]);
  };

  const handleSaveExisting = () => {
    if (!selectedApiId) return;
    setSaving(true);
    dataService.setButtonMapping({
      deviceId,
      buttonIndex: index,
      externalApiId: selectedApiId,
      apiActionId: isStopwatchExisting ? apiActionId : flowBoxButtonAction.MEASURE,
      updatedByUserId: userId,
    })
      .then(() => { toast.success(t("flowBox.modify.buttonMapped", { index })); setMode(null); onSaved(); })
      .catch(() => toast.error(t("flowBox.modify.mappingSaveFailed")))
      .finally(() => setSaving(false));
  };

  const handleCreateTrigger = () => {
    if (!triggerName.trim() || !projectDetailId) return;
    setSaving(true);
    dataService.createTriggerForButton({
      deviceId,
      buttonIndex: index,
      externalApiTypeId: triggerTypeId,
      projectDetailId,
      name: triggerName.trim(),
      companyId,
      insertedByUserId: userId,
    })
      .then(() => { toast.success(t("flowBox.modify.endpointCreatedAndMapped", { index })); setMode(null); onSaved(); })
      .catch(() => toast.error(t("flowBox.modify.endpointCreateFailed")))
      .finally(() => setSaving(false));
  };

  return (
    <div className={classes.buttonSlot}>
      <div className={classes.buttonSlotHeader}>
        <span className={classes.buttonIndex}>{t("flowBox.modify.button", { index })}</span>
        {mapping && (
          <span className={classes.currentMapping}>
            <ChevronRight size={14} style={{ opacity: 0.5 }} />
            {mapping.externalApiName || `API #${mapping.externalApiId}`}
          </span>
        )}
      </div>

      {mode === null && (
        <div className={classes.modeRow}>
          <Button size="small" variant="light" onClick={openUseExisting}>{t("flowBox.modify.useExistingEndpoint")}</Button>
          <Button size="small" variant="light" onClick={openCreateTrigger}>{t("flowBox.modify.createEndpoint")}</Button>
        </div>
      )}

      {mode === "use-existing" && (
        <div className={classes.modeContent}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t("flowBox.modify.endpointLabel")}</InputLabel>
            <MuiSelect
              value={selectedApiId}
              label={t("flowBox.modify.endpointLabel")}
              onChange={(e) => { setSelectedApiId(e.target.value); setApiActionId(flowBoxButtonAction.MEASURE); }}
            >
              {externalApis.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
          {isStopwatchExisting && (
            <FormControl size="small" fullWidth style={{ marginTop: "8px" }}>
              <InputLabel>{t("flowBox.modify.buttonActionLabel")}</InputLabel>
              <MuiSelect value={apiActionId} label={t("flowBox.modify.buttonActionLabel")} onChange={(e) => setApiActionId(e.target.value)}>
                {ACTION_OPTS_STOPWATCH.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          )}
          <div className={classes.modeActions}>
            <Button size="small" variant="light" onClick={() => setMode(null)}>{t("common.cancel")}</Button>
            <Button size="small" startIcon={<Save size={14} />} onClick={handleSaveExisting} disabled={!selectedApiId || saving}>
              {saving ? t("flowBox.modify.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      )}

      {mode === "create-trigger" && (
        <div className={classes.modeContent}>
          <TextField
            size="small"
            fullWidth
            label={t("flowBox.modify.endpointNameLabel")}
            value={triggerName}
            onChange={(e) => setTriggerName(e.target.value)}
            helperText={t("flowBox.modify.endpointNameHelper")}
          />
          <FormControl size="small" fullWidth style={{ marginTop: "8px" }}>
            <InputLabel>{t("common.type")}</InputLabel>
            <MuiSelect value={triggerTypeId} label={t("common.type")} onChange={(e) => { setTriggerTypeId(e.target.value); setNewTriggerAction(flowBoxButtonAction.TOGGLE); }}>
              <MenuItem value={externalApiTypes.DATA}>{t("flowBox.modify.typeCounterData")}</MenuItem>
              <MenuItem value={externalApiTypes.STOPWATCH}>{t("flowBox.endpoint.typeStopwatch")}</MenuItem>
            </MuiSelect>
          </FormControl>
          {isStopwatchNew && (
            <FormControl size="small" fullWidth style={{ marginTop: "8px" }}>
              <InputLabel>{t("flowBox.modify.buttonActionLabel")}</InputLabel>
              <MuiSelect value={newTriggerAction} label={t("flowBox.modify.buttonActionLabel")} onChange={(e) => setNewTriggerAction(e.target.value)}>
                {ACTION_OPTS_STOPWATCH.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          )}
          <FormControl size="small" fullWidth style={{ marginTop: "8px" }}>
            <InputLabel>{t("flowBox.modify.registrationPoint")}</InputLabel>
            <MuiSelect
              value={selectedProjectQr}
              label={t("flowBox.modify.registrationPoint")}
              onChange={(e) => { setSelectedProjectQr(e.target.value); setProjectDetailId(""); }}
              disabled={loadingProjects}
            >
              {loadingProjects ? (
                <MenuItem disabled>{t("common.loading")}</MenuItem>
              ) : projects.map((p) => (
                <MenuItem key={p.qrCode} value={p.qrCode}>{p.resourceDescription || p.projectName}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
          <FormControl size="small" fullWidth style={{ marginTop: "8px" }}>
            <InputLabel>{t("flowBox.modify.field")}</InputLabel>
            <MuiSelect
              value={projectDetailId}
              label={t("flowBox.modify.field")}
              onChange={(e) => setProjectDetailId(e.target.value)}
              disabled={!selectedProjectQr || loadingDetails}
            >
              {loadingDetails ? (
                <MenuItem disabled>{t("common.loading")}</MenuItem>
              ) : detailOptions.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
          <div className={classes.modeActions}>
            <Button size="small" variant="light" onClick={() => setMode(null)}>{t("common.cancel")}</Button>
            <Button size="small" startIcon={<Save size={14} />} onClick={handleCreateTrigger}
              disabled={!triggerName.trim() || !projectDetailId || saving}>
              {saving ? t("flowBox.modify.saving") : t("flowBox.modify.createAndBind")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────
function FlowBoxDeviceModifyPage(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const userId = props.Login.userid;

  const [device, setDevice] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [externalApis, setExternalApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [confirmDeregister, setConfirmDeregister] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const loadMappings = () =>
    dataService.getButtonMappingsByDeviceId(parseInt(deviceId, 10))
      .then((m) => setMappings(m || []));

  const load = () => {
    setLoading(true);
    dataService.getFlowBoxDeviceById(deviceId)
      .then((dev) => {
        setDevice(dev);
        setRenameValue(dev?.displayName || "");
        return Promise.all([
          dataService.getButtonMappingsByDeviceId(parseInt(deviceId, 10)),
          dataService.getAllCompaniesByUserId(userId).then((companies) =>
            Promise.all((companies || []).map((c) => dataService.getExternalApisByCompanyId(c.id)))
              .then((res) => res.flat().filter(Boolean))
          ),
        ]);
      })
      .then(([maps, apis]) => {
        setMappings(maps || []);
        setExternalApis(apis || []);
      })
      .catch(() => toast.error(t("flowBox.modify.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRename = () => {
    if (!renameValue.trim()) return;
    setRenameSaving(true);
    dataService.renameFlowBoxDevice({ deviceId: parseInt(deviceId, 10), displayName: renameValue.trim(), updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.modify.deviceRenamed")); setDevice((d) => ({ ...d, displayName: renameValue.trim() })); })
      .catch(() => toast.error(t("flowBox.modify.deviceRenameFailed")))
      .finally(() => setRenameSaving(false));
  };

  const handleDeregister = () => {
    setConfirmDeregister(false);
    dataService.deregisterFlowBoxDevice({ deviceId: parseInt(deviceId, 10), updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.modify.deviceDeregistered")); navigate("/flowboxoverview"); })
      .catch(() => toast.error(t("flowBox.modify.deviceDeregisterFailed")));
  };

  const handleDeactivate = () => {
    setConfirmDeactivate(false);
    dataService.deactivateFlowBoxDevice({ deviceId: parseInt(deviceId, 10), updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.modify.deviceDeactivated")); load(); })
      .catch(() => toast.error(t("flowBox.modify.deviceDeactivateFailed")));
  };

  const statusInfo = device ? (STATUS_INFO[device.statusId] || { tKey: "flowBox.status.unknown", color: "#9ca3af" }) : null;
  const buttonCount = device?.buttonCount || 5;

  if (loading) {
    return (
      <Box className="page-container">
        <HeaderBar><HeaderTitle>{t("flowBox.modify.title")}</HeaderTitle></HeaderBar>
        <div style={{ padding: "48px", textAlign: "center", color: "#9ca3af" }}>{t("common.loading")}</div>
      </Box>
    );
  }

  if (!device) {
    return (
      <Box className="page-container">
        <HeaderBar><HeaderTitle>{t("flowBox.modify.title")}</HeaderTitle></HeaderBar>
        <div style={{ padding: "48px", textAlign: "center", color: "#ef4444" }}>{t("flowBox.modify.deviceNotFound")}</div>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>{t("flowBox.modify.title")}</HeaderTitle>
        </HeaderBar>

        <BackButton onClick={() => navigate("/flowboxoverview")} />

        <div className={classes.layout}>
          {/* Left: device info + actions */}
          <div className={classes.sidebar}>
            <div className={classes.card}>
              <Label>{t("flowBox.modify.deviceInfo")}</Label>
              <div className={classes.infoGrid}>
                <span className={classes.infoLabel}>{t("flowBox.modify.fieldStatus")}</span>
                <span className={classes.statusBadge}
                  style={{ backgroundColor: statusInfo.color + "22", color: statusInfo.color, border: `1px solid ${statusInfo.color}` }}>
                  {t(statusInfo.tKey)}
                </span>
                <span className={classes.infoLabel}>{t("flowBox.modify.fieldImei")}</span>
                <span className={classes.imei}>{device.imei}</span>
                <span className={classes.infoLabel}>{t("flowBox.modify.fieldType")}</span>
                <span>{device.deviceTypeName || device.deviceTypeCode || "-"}</span>
                <span className={classes.infoLabel}>{t("flowBox.modify.fieldCommissioned")}</span>
                <span>{formatDate(device.commissionedOn)}</span>
                <span className={classes.infoLabel}>{t("flowBox.modify.fieldLastSeen")}</span>
                <span>{formatDate(device.lastSeenOn)}</span>
              </div>
            </div>

            <div className={classes.card}>
              <Label>{t("flowBox.modify.renameDevice")}</Label>
              <TextField
                size="small"
                fullWidth
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                label={t("flowBox.modify.displayNameLabel")}
                inputProps={{ maxLength: 100 }}
                style={{ marginBottom: "8px", marginTop: "8px" }}
              />
              <Button size="small" startIcon={<Pencil size={14} />} onClick={handleRename}
                disabled={renameSaving || !renameValue.trim()}>
                {renameSaving ? t("flowBox.modify.saving") : t("flowBox.modify.saveName")}
              </Button>
            </div>

            <div className={classes.card}>
              <Label>{t("flowBox.modify.actions")}</Label>
              <div className={classes.actionRow}>
                <Button size="small" variant="light" startIcon={<PowerOff size={14} />}
                  onClick={() => setConfirmDeactivate(true)}
                  disabled={device.statusId === flowBoxDeviceStatus.INACTIVE || device.statusId === flowBoxDeviceStatus.DEREGISTERED}>
                  {t("flowBox.modify.deactivate")}
                </Button>
                <Button size="small" variant="light" startIcon={<Trash2 size={14} />}
                  style={{ color: "#ef4444", borderColor: "#ef4444" }}
                  onClick={() => setConfirmDeregister(true)}
                  disabled={device.statusId === flowBoxDeviceStatus.DEREGISTERED}>
                  {t("flowBox.modify.deregister")}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: button mapping panel */}
          <div className={classes.main}>
            <div className={classes.card}>
              <Label>{t("flowBox.modify.buttonMappings")}</Label>
              <p className={classes.hint}>
                {t("flowBox.modify.buttonMappingsHint")}
              </p>
              <Divider style={{ margin: "12px 0" }} />
              {Array.from({ length: buttonCount }, (_, i) => i + 1).map((btnIdx) => (
                <React.Fragment key={btnIdx}>
                  <ButtonSlot
                    index={btnIdx}
                    mapping={mappings.find((m) => m.buttonIndex === btnIdx) || null}
                    externalApis={externalApis}
                    companyId={device.companyId}
                    userId={userId}
                    deviceId={parseInt(deviceId, 10)}
                    onSaved={loadMappings}
                  />
                  {btnIdx < buttonCount && <Divider style={{ margin: "8px 0" }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </Box>

      <Dialog open={confirmDeregister} onClose={() => setConfirmDeregister(false)}>
        <DialogTitle>{t("flowBox.modify.deregisterTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("flowBox.modify.deregisterMessage", { name: device.displayName || device.imei })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="light" size="small" onClick={() => setConfirmDeregister(false)}>{t("common.cancel")}</Button>
          <Button size="small" onClick={handleDeregister}>{t("flowBox.modify.deregister")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeactivate} onClose={() => setConfirmDeactivate(false)}>
        <DialogTitle>{t("flowBox.modify.deactivateTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("flowBox.modify.deactivateMessage", { name: device.displayName || device.imei })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="light" size="small" onClick={() => setConfirmDeactivate(false)}>{t("common.cancel")}</Button>
          <Button size="small" onClick={handleDeactivate}>{t("flowBox.modify.deactivate")}</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default FlowBoxDeviceModifyPage;

const useStyles = makeStyles((theme) => ({
  layout: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
    marginBottom: "48px",
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    [theme.breakpoints.up("md")]: { width: "300px", flexShrink: 0 },
  },
  main: { flex: 1, minWidth: 0 },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #EDEDED",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "8px 16px",
    alignItems: "center",
    marginTop: "8px",
    fontSize: "14px",
  },
  infoLabel: { color: "#6b7280", fontWeight: "500" },
  imei: { fontFamily: "monospace", fontSize: "13px" },
  statusBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "500",
    width: "fit-content",
  },
  actionRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" },
  hint: { fontSize: "13px", color: "#6b7280", margin: "4px 0 0" },
  buttonSlot: { padding: "4px 0" },
  buttonSlotHeader: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
  buttonIndex: { fontWeight: "600", fontSize: "14px", color: "#0C0E29" },
  currentMapping: { display: "flex", alignItems: "center", fontSize: "13px", color: "#6b7280" },
  modeRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  modeContent: { display: "flex", flexDirection: "column", gap: "8px" },
  modeActions: { display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "4px" },
}));
